import { el, voteIcon } from './utils';
import type { EmbedPoll, VoteMap } from './utils';
import { toggleVote } from './vote-toggle';
import { computeCounts, renderResultBar, renderBadges } from './render-shared';

/**
 * Render an option ("what") poll grid.
 */
export function renderOptionPoll(
	poll: EmbedPoll,
	votes: VoteMap,
	onVotesChange: (votes: VoteMap) => void,
	disabled: boolean
): HTMLElement {
	const options = (poll.options || []).sort((a, b) => a.position - b.position);
	const settings = poll.settings || {};

	// Compute vote counts per option
	const counts = computeCounts(poll, options.map((o) => o.id));
	const maxVotes = Math.max(...Object.values(counts).map((c) => c.yes + c.maybe + c.no), 1);

	const grid = el('div', { class: 'dte-grid' });

	for (const option of options) {
		const c = counts[option.id] || { yes: 0, maybe: 0, no: 0 };
		const currentResponse = votes[option.id];

		// Vote button
		const btnClass = currentResponse ? `dte-vote-btn dte-vote-btn-${currentResponse}` : 'dte-vote-btn';
		const btn = el('button', {
			class: btnClass,
			type: 'button',
			disabled: disabled,
			'aria-label': `Vote ${currentResponse || 'empty'} for ${option.label}`
		}, [voteIcon(currentResponse)]);

		if (!disabled) {
			btn.addEventListener('click', () => {
				const newVotes = toggleVote(option.id, votes, settings);
				for (const key of Object.keys(votes)) delete votes[key];
				for (const [k, v] of Object.entries(newVotes)) votes[k] = v;
				onVotesChange(votes);
			});
		}

		// Label
		const label = el('div', { class: 'dte-label' }, [option.label]);

		// Result bar
		const result = renderResultBar(c, maxVotes);

		const row = el('div', { class: 'dte-row' }, [btn, label, result]);
		grid.appendChild(row);

		// Voter badges (unless anonymous)
		if (!settings.anonymous_voting) {
			const badges = renderBadges(poll, option.id);
			if (badges) grid.appendChild(badges);
		}
	}

	return grid;
}
